import { useEffect, useState } from "react";
import { useDevices } from "../../../hooks/useDevices";
import Modal, { MODAL_SIZES } from "../modal/Modal";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { Scanner } from "@yudiel/react-qr-scanner";
import { useCamera } from "../../../hooks/useCamera";
import { AxiosError } from "axios";
import DevicePhotos, { FileStateType } from "../../photos";

type AdoptDeviceModalProps = {
  onClose: () => void;
};

enum MODE {
  SCAN_OR_ENTER,
  EDIT,
}

const defaultConstraints: MediaTrackConstraints = {
  facingMode: "environment",
  width: { min: 640, ideal: 720, max: 1920 },
  height: { min: 640, ideal: 720, max: 1080 },
};

const ERROR_TOAST_STYLE = {
  className: "bg-red-600 text-zinc-50",
  progressClassName: "opacity-90 [background:#fafafa]",
};

const adoptDeviceFailureNotification = (msg?: string) =>
  toast(
    "Cannot adopt device: " + msg ||
      "Something went wrong adopting the device. Try again later.",
    ERROR_TOAST_STYLE
  );
const addDeviceFailureNotification = () =>
  toast(
    "Something went wrong adding the device. Try again later.",
    ERROR_TOAST_STYLE
  );
const cameraPermissionErrorNotification = () =>
  toast(
    "Failed to access the camera. Please check your device settings and permissions.",
    ERROR_TOAST_STYLE
  );

const AdoptDeviceModal = ({ onClose }: AdoptDeviceModalProps) => {
  const { adoptDevice, updateDeviceById, savePhotos } = useDevices();
  const { register, watch, reset, setValue } = useForm({
    defaultValues: {
      deviceId: "",
      deviceName: "",
      notes: "",
    },
  });
  const [recheckCameraPermission, setRecheckCameraPermission] = useState(false);
  const [currentMode, setCurrentMode] = useState(MODE.SCAN_OR_ENTER);
  const [adopting, setAdopting] = useState(false);
  const [addingDevice, setAddingDevice] = useState(false);
  const [photos, setPhotos] = useState<FileStateType[]>([])

  const { state: cameraPermissionState, error: errorOnCameraPermission } =
    useCamera(recheckCameraPermission);

  // Initial state will be based on current permission state
  // Future state(s) will be based on user actions
  const [enableCamera, setEnableCamera] = useState(
    cameraPermissionState === "granted" ? true : false
  );

  const deviceIdValue = watch("deviceId");
  const deviceNameValue = watch("deviceName");
  const noteValue = watch("notes");

  const handleContinue = async (adoptDeviceIdValue: string) => {
    if (adoptDeviceIdValue.length < 24) {
      return;
    }

    setAdopting(true);

    try {
      await adoptDevice(adoptDeviceIdValue);

      toast.success("Device adopted successfully");

      setCurrentMode(MODE.EDIT);
    } catch (err: AxiosError | any) {
      console.error("adopt device failure", err.response.data.error);
      adoptDeviceFailureNotification(err.response.data.error);
    } finally {
      setAdopting(false);
    }
  };

  const handleAddDevice = async () => {
    if (deviceNameValue.length === 0) {
      return;
    }

    setAddingDevice(true);

    try {
      await updateDeviceById(deviceIdValue, {
        name: deviceNameValue,
        ...(noteValue.length > 0 ? { notes: noteValue } : {}),
      });

      await savePhotos(photos, deviceIdValue)

      onClose();
    } catch (err) {
      addDeviceFailureNotification();
    } finally {
      setAddingDevice(false);
    }
  };

  const handleAddAnotherDevice = async () => {
    await handleAddDevice();

    reset();
    setCurrentMode(MODE.SCAN_OR_ENTER);
  };

  const handleRequestCameraAccess = async () => {
    if (!errorOnCameraPermission) {
      try {
        // Initially, I had the handler only set enableCamera to true
        // But the <Scanner /> component has a bug - seen when the user
        // declines camera permission - https://github.com/yudielcurbelo/react-qr-scanner/issues/113
        // Hence, we request camera access ourselves instead
        // !IMPORTANT - (With the same constraints as the Scanner component
        // !IMPORTANT - otherwise you can get a OverconstrainedError from getUserMedia())
        // and handle the error ourselves.
        await navigator.mediaDevices.getUserMedia({
          video: defaultConstraints,
          audio: false,
        });

        setRecheckCameraPermission(true);
      } catch (err: unknown) {
        setRecheckCameraPermission(true);
      }
    }
  };

  const handleScanResult = async (result: IDetectedBarcode[]) => {
    const scannedItem = result[0];

    if (scannedItem.format === "qr_code" && scannedItem.rawValue.length > 0) {
      const deviceIdString = scannedItem.rawValue as string;

      const deviceId = deviceIdString.toLowerCase().split("/").pop();
      if (deviceId) {
        setValue("deviceId", deviceId);

        await handleContinue(deviceId);
      }
    }
  };

  useEffect(() => {
    if (cameraPermissionState === "granted" && !enableCamera) {
      setEnableCamera(true);
    } else if (cameraPermissionState === "denied") {
      setEnableCamera(false);
      cameraPermissionErrorNotification();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraPermissionState]);

  return (
    <Modal title="Add a Device" onClose={onClose} size={MODAL_SIZES.MEDIUM}>
      <>
        <p className="text-slate-400 mb-4">
          Scan the QR code on top of your device or enter the device id manually
          below. Mobile setup recommended for simplicity
        </p>
        <form onSubmit={(evt) => evt.preventDefault()}>
          {currentMode === MODE.SCAN_OR_ENTER && (
            <div className="h-48">
              {(cameraPermissionState === "prompt" ||
                cameraPermissionState === "denied") && (
                <div className="flex flex-col items-center gap-4 justify-center h-full bg-blue-50 border-b-2 border-slate-200 p-4">
                  <p className="uppercase text-center">
                    Enable camera access to scan QR code
                  </p>

                  <button
                    className="px-4 py-3 border border-transparent rounded-md shadow-sm text-xs font-bold uppercase tracking-wider text-white bg-blue-700 hover:bg-blue-800 disabled:pointer-events-none disabled:opacity-50 disabled:bg-slate-300 disabled:text-slate-600"
                    type="button"
                    onClick={() => handleRequestCameraAccess()}
                  >
                    Enable Camera
                  </button>
                </div>
              )}
              {enableCamera && (
                <Scanner
                  components={{
                    finder: false,
                    audio: false,
                  }}
                  constraints={defaultConstraints}
                  classNames={{ container: "max-w-full h-48" }}
                  onScan={handleScanResult}
                  onError={() => cameraPermissionErrorNotification()}
                />
              )}
            </div>
          )}
          <div>
            <label
              htmlFor="deviceId"
              className="block text-left text-xs font-bold text-gray-700 uppercase tracking-wider mt-3"
            >
              Device ID
            </label>
            <input
              id="deviceId"
              className="w-full text-base md:text-sm text-gray-900 border-2 border-gray-100 rounded-md px-3 py-2 mb-4 mt-2"
              {...register("deviceId")}
              placeholder="Enter device ID"
              disabled={currentMode === MODE.EDIT}
            />
          </div>

          {currentMode === MODE.EDIT && (
            <>
              <div>
                <label
                  htmlFor="deviceName"
                  className="block text-left text-xs font-bold text-gray-700 uppercase tracking-wider mt-3"
                >
                  Name device<sup>*</sup>
                </label>
                <p className="text-slate-400 text-[0.8rem] my-2">
                  Device names should indicate location, e.g., 'Conference Room
                  One'. You can edit this later in Device Settings
                </p>
                <input
                  id="deviceName"
                  className="w-full text-base md:text-sm text-gray-900 border-2 border-gray-100 rounded-md px-3 py-2 mb-4 mt-2"
                  {...register("deviceName")}
                  placeholder="Device Name"
                />
              </div>

              <div>
                <label
                  htmlFor="notes"
                  className="block text-left text-xs font-bold text-gray-700 uppercase tracking-wider mt-3"
                >
                  Add Notes
                </label>
                <p className="text-slate-400 text-[0.8rem] my-2">
                  Add descriptive details such as platn type and location for
                  easy device identification. You can edit this later in Device
                  Settings
                </p>
                <textarea
                  id="notes"
                  className="w-full text-base md:text-sm text-gray-900 border-2 border-gray-100 rounded-md px-3 py-2 mb-4 mt-2"
                  {...register("notes")}
                  placeholder="Add Notes"
                  rows={4}
                />
              </div>

              <DevicePhotos
                photos={photos}
                setPhotos={setPhotos}
              />

              {/* <div>
                <button
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring text-blue-800 hover:text-slate-900 hover:underline mb-4 disabled:text-slate-200"
                  type="button"
                  disabled={deviceNameValue.length === 0}
                  onClick={handleAddAnotherDevice}
                >
                  Add Another Device
                </button>
              </div> */}
            </>
          )}

          <div className="flex justify-end pt-4 border-t border-slate-200 gap-8">
            <button
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring px-4 py-3 hover:bg-slate-200 hover-text-slate-600"
              type="button"
              onClick={onClose}
            >
              Cancel
            </button>

            {currentMode === MODE.EDIT && (
              <button
                className="px-4 py-3 border border-transparent rounded-md shadow-sm text-xs font-bold uppercase tracking-wider text-white bg-blue-700 hover:bg-blue-800 disabled:pointer-events-none disabled:opacity-50 disabled:bg-slate-300 disabled:text-slate-600"
                type="button"
                onClick={handleAddDevice}
                // disabled
              >
                {addingDevice ? "Saving..." : "Add This Device"}
              </button>
            )}

            {currentMode === MODE.SCAN_OR_ENTER && (
              <button
                className="px-4 py-3 border border-transparent rounded-md shadow-sm text-xs font-bold uppercase tracking-wider text-white bg-blue-700 hover:bg-blue-800 disabled:pointer-events-none disabled:opacity-50 disabled:bg-slate-300 disabled:text-slate-600"
                type="button"
                onClick={() => handleContinue(deviceIdValue)}
                disabled={deviceIdValue.length < 24 || adopting}
              >
                {adopting ? "Wait..." : "Continue"}
              </button>
            )}
          </div>
        </form>
      </>
    </Modal>
  );
};

export default AdoptDeviceModal;
